import type { G702Item } from "@/lib/types";

/**
 * AIA G702/G703 payment-application math. Ported from the prototype's ivG*
 * functions and kept as pure functions so they can be unit-tested — this is
 * the product's differentiator, so the numbers must be exactly right.
 * See g702Math.test.ts for verification against a known application.
 */

/**
 * Re-exported rather than duplicated (backlog UX-14) — this used to be a
 * byte-for-byte copy of @/features/quotes/quoteMath's money()/moneyExact(),
 * manually "kept in sync" by comment instead of actually shared. Every
 * existing import of `money`/`moneyExact` from this module keeps working
 * unchanged.
 */
export { money, moneyExact } from "@/features/quotes/quoteMath";

export interface G702LineCalc {
  scheduled: number;
  prev: number;
  thisPeriod: number;
  stored: number;
  totalToDate: number;
  pct: number;
  balance: number;
  /** Column I of the G703 — retainage held against this line's total-to-date. */
  retainage: number;
  /** Billing past the scheduled value is the #1 reason a GC kicks back a pay app. */
  overbilled: boolean;
}

/** Per continuation-sheet line (one row of the G703). `retainagePct` defaults to 0 for callers that don't need column I (e.g. the cover-page totals). */
export function lineCalc(item: G702Item, retainagePct = 0): G702LineCalc {
  const scheduled = item.scheduledValue || 0;
  const prev = item.prevCompleted || 0;
  const thisPeriod = item.thisPeriod || 0;
  const stored = item.materialsStored || 0;
  const totalToDate = prev + thisPeriod + stored;
  const pct = scheduled > 0 ? (totalToDate / scheduled) * 100 : 0;
  const balance = scheduled - totalToDate;
  const retainage = totalToDate * (retainagePct / 100);
  return { scheduled, prev, thisPeriod, stored, totalToDate, pct, balance, retainage, overbilled: totalToDate > scheduled };
}

export interface G702Input {
  contractSum: number;
  changeOrders: number;
  retainagePct: number;
  /** Line 7 override. `null`/undefined auto-derives it from prior billings (sum of each line's `prevCompleted`, net of retainage on that sum) — matches how per-client tax/markup overrides work elsewhere: a number is an explicit override, absence means "compute it". */
  previousCerts?: number | null;
}

export interface G702Totals {
  contractSum: number;
  changeOrders: number;
  retainagePct: number;
  totalScheduled: number;
  totalCompletedToDate: number;
  contractSumToDate: number;
  /** 5a — retainage held on completed (not-stored) work. */
  retainageCompleted: number;
  /** 5b — retainage held on stored materials. */
  retainageStored: number;
  /** 5 = 5a + 5b. */
  retainageAmount: number;
  totalEarnedLessRetainage: number;
  /** Line 7, resolved — either the caller's override or the auto-derived value. */
  previousCerts: number;
  currentPaymentDue: number;
  balanceToFinish: number;
  /** Schedule of values must foot to the contract sum to date, or a GC rejects the sheet. */
  scheduleVariance: number;
}

/** The G702 cover-page totals. Ported from ivGComputeTotals, extended with the 5a/5b retainage split and the schedule-variance reconciliation check. */
export function computeG702Totals(items: G702Item[], input: G702Input): G702Totals {
  let totalScheduled = 0;
  let totalCompletedToDate = 0;
  let storedToDate = 0;
  let previousToDate = 0;
  for (const it of items) {
    const c = lineCalc(it);
    totalScheduled += c.scheduled;
    totalCompletedToDate += c.totalToDate;
    storedToDate += c.stored;
    previousToDate += c.prev;
  }
  const completedToDate = totalCompletedToDate - storedToDate;
  const contractSumToDate = input.contractSum + input.changeOrders;

  const retainageCompleted = completedToDate * (input.retainagePct / 100);
  const retainageStored = storedToDate * (input.retainagePct / 100);
  const retainageAmount = retainageCompleted + retainageStored;

  const totalEarnedLessRetainage = totalCompletedToDate - retainageAmount;
  const previousCerts =
    input.previousCerts ?? previousToDate - previousToDate * (input.retainagePct / 100);
  const currentPaymentDue = totalEarnedLessRetainage - previousCerts;
  const balanceToFinish = contractSumToDate - totalEarnedLessRetainage;

  return {
    contractSum: input.contractSum,
    changeOrders: input.changeOrders,
    retainagePct: input.retainagePct,
    totalScheduled,
    totalCompletedToDate,
    contractSumToDate,
    retainageCompleted,
    retainageStored,
    retainageAmount,
    totalEarnedLessRetainage,
    previousCerts,
    currentPaymentDue,
    balanceToFinish,
    scheduleVariance: totalScheduled - contractSumToDate,
  };
}
